import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import {
  Users,
  Plus,
  Search,
  Edit2,
  Trash2,
  X,
  Shield,
  ShieldCheck,
  ShieldAlert,
  Mail,
  UserCheck,
  UserX,
  Crown,
  Send,
  RefreshCw,
  CheckCircle,
  Clock,
} from 'lucide-react';
import { db } from '../../services/firebase';
import {
  collection,
  addDoc,
  updateDoc,
  deleteDoc,
  doc,
  onSnapshot,
  query,
  orderBy,
} from 'firebase/firestore';

const roles = [
  {
    id: 'admin',
    name: 'Admin',
    description: 'Full access to all features',
    icon: Crown,
    color: 'text-yellow-500',
    bgColor: 'bg-yellow-500/20',
  },
  {
    id: 'editor',
    name: 'Editor',
    description: 'Can manage content (music, shows, merch)',
    icon: ShieldCheck,
    color: 'text-blue-500',
    bgColor: 'bg-blue-500/20',
  },
  {
    id: 'booking_manager',
    name: 'Booking Manager',
    description: 'Can manage bookings and calendar',
    icon: Shield,
    color: 'text-green-500',
    bgColor: 'bg-green-500/20',
  },
];

export default function AdminUsers() {
  const [users, setUsers] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    role: 'editor',
    status: 'active',
    sendInvite: true,
  });
  const [isSendingInvite, setIsSendingInvite] = useState(false);

  // Fetch users from Firestore
  useEffect(() => {
    if (!db) return;

    const q = query(collection(db, 'users'), orderBy('createdAt', 'desc'));
    const unsubscribe = onSnapshot(q, (snapshot) => {
      const usersData = snapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));
      setUsers(usersData);
    });

    return unsubscribe;
  }, []);

  const handleOpenModal = (user = null) => {
    if (user) {
      setEditingUser(user);
      setFormData({
        name: user.name || '',
        email: user.email || '',
        role: user.role || 'editor',
        status: user.status || 'active',
        sendInvite: false, // Don't show invite option when editing
      });
    } else {
      setEditingUser(null);
      setFormData({
        name: '',
        email: '',
        role: 'editor',
        status: 'active',
        sendInvite: true, // Default to sending invite for new members
      });
    }
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setEditingUser(null);
    setFormData({
      name: '',
      email: '',
      role: 'editor',
      status: 'active',
      sendInvite: true,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!db) {
      alert('Database not connected. Please refresh the page and try again.');
      return;
    }

    if (!formData.name || !formData.email) {
      alert('Please fill in all required fields.');
      return;
    }

    setIsLoading(true);

    try {
      const { sendInvite, ...userData } = formData;

      if (editingUser) {
        await updateDoc(doc(db, 'users', editingUser.id), {
          ...userData,
          updatedAt: new Date(),
        });
        alert('Team member updated successfully!');
      } else {
        // Add new user
        const newUserData = {
          ...userData,
          createdAt: new Date(),
          updatedAt: new Date(),
          inviteStatus: sendInvite ? 'pending' : 'none',
        };

        const docRef = await addDoc(collection(db, 'users'), newUserData);

        // Send invite email if checkbox is checked
        if (sendInvite) {
          setIsSendingInvite(true);
          try {
            const response = await fetch('/api/send-team-invite', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({
                email: formData.email,
                name: formData.name,
                role: formData.role,
              }),
            });

            const result = await response.json();

            if (result.success) {
              // Update invite status
              await updateDoc(doc(db, 'users', docRef.id), {
                inviteStatus: 'sent',
                inviteSentAt: new Date(),
                firebaseUid: result.uid,
              });
              alert('Team member added and invite sent successfully!');
            } else {
              // Mark as failed but keep the user
              await updateDoc(doc(db, 'users', docRef.id), {
                inviteStatus: 'failed',
                inviteError: result.error,
              });
              alert(`Team member added, but invite failed: ${result.error}`);
            }
          } catch (inviteError) {
            console.error('Error sending invite:', inviteError);
            await updateDoc(doc(db, 'users', docRef.id), {
              inviteStatus: 'failed',
              inviteError: inviteError.message,
            });
            alert(`Team member added, but invite failed: ${inviteError.message}`);
          } finally {
            setIsSendingInvite(false);
          }
        } else {
          alert('Team member added successfully!');
        }
      }

      handleCloseModal();
    } catch (error) {
      console.error('Error saving user:', error);
      alert(`Failed to save user: ${error.message || 'Unknown error'}`);
    } finally {
      setIsLoading(false);
      setIsSendingInvite(false);
    }
  };

  const handleResendInvite = async (user) => {
    if (!window.confirm(`Resend invite to ${user.email}?`)) return;

    setIsSendingInvite(true);
    try {
      const response = await fetch('/api/send-team-invite', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: user.email,
          name: user.name,
          role: user.role,
        }),
      });

      const result = await response.json();

      if (result.success) {
        await updateDoc(doc(db, 'users', user.id), {
          inviteStatus: 'sent',
          inviteSentAt: new Date(),
          firebaseUid: result.uid,
        });
        alert('Invite resent successfully!');
      } else {
        alert(`Failed to resend invite: ${result.error}`);
      }
    } catch (error) {
      console.error('Error resending invite:', error);
      alert(`Failed to resend invite: ${error.message}`);
    } finally {
      setIsSendingInvite(false);
    }
  };

  const handleDelete = async (userId) => {
    if (!window.confirm('Are you sure you want to remove this team member?')) return;

    try {
      await deleteDoc(doc(db, 'users', userId));
    } catch (error) {
      console.error('Error deleting user:', error);
      alert('Failed to delete user. Please try again.');
    }
  };

  const toggleStatus = async (user) => {
    try {
      await updateDoc(doc(db, 'users', user.id), {
        status: user.status === 'active' ? 'inactive' : 'active',
        updatedAt: new Date(),
      });
    } catch (error) {
      console.error('Error updating user status:', error);
    }
  };

  const filteredUsers = users.filter(
    (user) =>
      user.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.email?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const getRoleInfo = (roleId) => roles.find((r) => r.id === roleId) || roles[1];

  // Stats
  const stats = {
    total: users.length,
    active: users.filter((u) => u.status === 'active').length,
    admins: users.filter((u) => u.role === 'admin').length,
    editors: users.filter((u) => u.role === 'editor').length,
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold text-white">Team Members</h2>
          <p className="text-gray-400">Manage admin users and their permissions</p>
        </div>
        <Button
          onClick={() => handleOpenModal()}
          className="bg-gradient-to-r from-orange-500 to-red-600 hover:from-orange-600 hover:to-red-700"
        >
          <Plus className="w-4 h-4 mr-2" />
          Add Team Member
        </Button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-500/20 rounded-lg">
                <Users className="w-5 h-5 text-blue-500" />
              </div>
              <div>
                <p className="text-2xl font-bold text-white">{stats.total}</p>
                <p className="text-xs text-gray-400">Total Members</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-green-500/20 rounded-lg">
                <UserCheck className="w-5 h-5 text-green-500" />
              </div>
              <div>
                <p className="text-2xl font-bold text-white">{stats.active}</p>
                <p className="text-xs text-gray-400">Active</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-yellow-500/20 rounded-lg">
                <Crown className="w-5 h-5 text-yellow-500" />
              </div>
              <div>
                <p className="text-2xl font-bold text-white">{stats.admins}</p>
                <p className="text-xs text-gray-400">Admins</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-500/20 rounded-lg">
                <ShieldCheck className="w-5 h-5 text-blue-500" />
              </div>
              <div>
                <p className="text-2xl font-bold text-white">{stats.editors}</p>
                <p className="text-xs text-gray-400">Editors</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Search */}
      <Card>
        <CardContent className="p-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <Input
              placeholder="Search team members..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
            />
          </div>
        </CardContent>
      </Card>

      {/* Users List */}
      <div className="grid gap-4">
        {filteredUsers.length === 0 ? (
          <Card>
            <CardContent className="p-12 text-center">
              <Users className="w-12 h-12 text-gray-600 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-white mb-2">No team members found</h3>
              <p className="text-gray-400 mb-4">
                {searchTerm ? 'Try a different search term' : 'Add your first team member to get started'}
              </p>
              {!searchTerm && (
                <Button onClick={() => handleOpenModal()}>
                  <Plus className="w-4 h-4 mr-2" />
                  Add Team Member
                </Button>
              )}
            </CardContent>
          </Card>
        ) : (
          filteredUsers.map((user) => {
            const roleInfo = getRoleInfo(user.role);
            const RoleIcon = roleInfo.icon;

            return (
              <motion.div
                key={user.id}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
              >
                <Card className="hover:border-gray-700 transition-colors">
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        {/* Avatar */}
                        <div className={`w-12 h-12 rounded-full ${roleInfo.bgColor} flex items-center justify-center`}>
                          <span className={`text-lg font-bold ${roleInfo.color}`}>
                            {user.name?.charAt(0)?.toUpperCase() || '?'}
                          </span>
                        </div>

                        {/* Info */}
                        <div>
                          <div className="flex items-center gap-2">
                            <h3 className="font-semibold text-white">{user.name || 'Unnamed'}</h3>
                            {user.status === 'inactive' && (
                              <span className="px-2 py-0.5 text-xs bg-gray-700 text-gray-400 rounded">
                                Inactive
                              </span>
                            )}
                            {user.inviteStatus === 'sent' && (
                              <span className="px-2 py-0.5 text-xs bg-blue-500/20 text-blue-400 rounded flex items-center gap-1">
                                <Clock className="w-3 h-3" />
                                Invite Sent
                              </span>
                            )}
                            {user.inviteStatus === 'failed' && (
                              <span className="px-2 py-0.5 text-xs bg-red-500/20 text-red-400 rounded">
                                Invite Failed
                              </span>
                            )}
                          </div>
                          <p className="text-sm text-gray-400 flex items-center gap-1">
                            <Mail className="w-3 h-3" />
                            {user.email}
                          </p>
                        </div>
                      </div>

                      <div className="flex items-center gap-4">
                        {/* Role Badge */}
                        <div className={`flex items-center gap-2 px-3 py-1.5 rounded-lg ${roleInfo.bgColor}`}>
                          <RoleIcon className={`w-4 h-4 ${roleInfo.color}`} />
                          <span className={`text-sm font-medium ${roleInfo.color}`}>{roleInfo.name}</span>
                        </div>

                        {/* Actions */}
                        <div className="flex items-center gap-2">
                          {(user.inviteStatus === 'sent' || user.inviteStatus === 'failed' || user.inviteStatus === 'pending') && (
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => handleResendInvite(user)}
                              disabled={isSendingInvite}
                              title="Resend Invite"
                              className="text-blue-500 hover:text-blue-400"
                            >
                              <RefreshCw className={`w-4 h-4 ${isSendingInvite ? 'animate-spin' : ''}`} />
                            </Button>
                          )}
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => toggleStatus(user)}
                            title={user.status === 'active' ? 'Deactivate' : 'Activate'}
                          >
                            {user.status === 'active' ? (
                              <UserX className="w-4 h-4 text-gray-400" />
                            ) : (
                              <UserCheck className="w-4 h-4 text-green-500" />
                            )}
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => handleOpenModal(user)}
                          >
                            <Edit2 className="w-4 h-4" />
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => handleDelete(user.id)}
                            className="text-red-500 hover:text-red-400"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            );
          })
        )}
      </div>

      {/* Role Permissions Info */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <ShieldAlert className="w-5 h-5 text-orange-500" />
            Role Permissions
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid md:grid-cols-3 gap-4">
            {roles.map((role) => {
              const Icon = role.icon;
              return (
                <div key={role.id} className={`p-4 rounded-lg ${role.bgColor}`}>
                  <div className="flex items-center gap-2 mb-2">
                    <Icon className={`w-5 h-5 ${role.color}`} />
                    <span className={`font-semibold ${role.color}`}>{role.name}</span>
                  </div>
                  <p className="text-sm text-gray-400">{role.description}</p>
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {/* Add/Edit Modal */}
      {isModalOpen && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-gray-900 rounded-xl max-w-md w-full max-h-[90vh] overflow-y-auto"
          >
            <div className="flex items-center justify-between p-6 border-b border-gray-800">
              <h3 className="text-xl font-bold text-white">
                {editingUser ? 'Edit Team Member' : 'Add Team Member'}
              </h3>
              <button
                onClick={handleCloseModal}
                className="text-gray-400 hover:text-white"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="p-6 space-y-4">
              <div className="space-y-2">
                <Label htmlFor="name">Full Name</Label>
                <Input
                  id="name"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="John Doe"
                  required
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="email">Email Address</Label>
                <Input
                  id="email"
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  placeholder="john@example.com"
                  required
                />
                <p className="text-xs text-gray-500">
                  {editingUser
                    ? 'This email must match the Firebase Auth email for login access'
                    : 'An invite will be sent to this email with instructions to set up their password'}
                </p>
              </div>

              <div className="space-y-2">
                <Label>Role</Label>
                <div className="grid gap-2">
                  {roles.map((role) => {
                    const Icon = role.icon;
                    return (
                      <button
                        key={role.id}
                        type="button"
                        onClick={() => setFormData({ ...formData, role: role.id })}
                        className={`flex items-center gap-3 p-3 rounded-lg border transition-all text-left ${
                          formData.role === role.id
                            ? `border-orange-500 ${role.bgColor}`
                            : 'border-gray-800 hover:border-gray-700'
                        }`}
                      >
                        <Icon className={`w-5 h-5 ${role.color}`} />
                        <div>
                          <p className="font-medium text-white">{role.name}</p>
                          <p className="text-xs text-gray-400">{role.description}</p>
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>

              <div className="space-y-2">
                <Label>Status</Label>
                <div className="flex gap-3">
                  <button
                    type="button"
                    onClick={() => setFormData({ ...formData, status: 'active' })}
                    className={`flex-1 flex items-center justify-center gap-2 p-3 rounded-lg border transition-all ${
                      formData.status === 'active'
                        ? 'border-green-500 bg-green-500/20 text-green-500'
                        : 'border-gray-800 text-gray-400 hover:border-gray-700'
                    }`}
                  >
                    <UserCheck className="w-4 h-4" />
                    Active
                  </button>
                  <button
                    type="button"
                    onClick={() => setFormData({ ...formData, status: 'inactive' })}
                    className={`flex-1 flex items-center justify-center gap-2 p-3 rounded-lg border transition-all ${
                      formData.status === 'inactive'
                        ? 'border-gray-500 bg-gray-500/20 text-gray-400'
                        : 'border-gray-800 text-gray-400 hover:border-gray-700'
                    }`}
                  >
                    <UserX className="w-4 h-4" />
                    Inactive
                  </button>
                </div>
              </div>

              {/* Send Invite Option - Only for new users */}
              {!editingUser && (
                <div className="space-y-2">
                  <button
                    type="button"
                    onClick={() => setFormData({ ...formData, sendInvite: !formData.sendInvite })}
                    className={`w-full flex items-center gap-3 p-4 rounded-lg border transition-all ${
                      formData.sendInvite
                        ? 'border-orange-500 bg-orange-500/10'
                        : 'border-gray-800 hover:border-gray-700'
                    }`}
                  >
                    <div className={`w-5 h-5 rounded border-2 flex items-center justify-center transition-colors ${
                      formData.sendInvite
                        ? 'bg-orange-500 border-orange-500'
                        : 'border-gray-600'
                    }`}>
                      {formData.sendInvite && <CheckCircle className="w-4 h-4 text-white" />}
                    </div>
                    <div className="text-left">
                      <div className="flex items-center gap-2">
                        <Send className={`w-4 h-4 ${formData.sendInvite ? 'text-orange-500' : 'text-gray-400'}`} />
                        <span className={formData.sendInvite ? 'text-white' : 'text-gray-400'}>
                          Send invite email
                        </span>
                      </div>
                      <p className="text-xs text-gray-500 mt-1">
                        They'll receive an email with a link to set up their password
                      </p>
                    </div>
                  </button>
                </div>
              )}

              <div className="flex gap-3 pt-4">
                <Button
                  type="button"
                  variant="outline"
                  onClick={handleCloseModal}
                  className="flex-1"
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  disabled={isLoading || isSendingInvite}
                  className="flex-1 bg-gradient-to-r from-orange-500 to-red-600 hover:from-orange-600 hover:to-red-700"
                >
                  {isLoading || isSendingInvite
                    ? isSendingInvite
                      ? 'Sending Invite...'
                      : 'Saving...'
                    : editingUser
                    ? 'Update'
                    : formData.sendInvite
                    ? 'Add & Send Invite'
                    : 'Add Member'}
                </Button>
              </div>
            </form>
          </motion.div>
        </div>
      )}
    </div>
  );
}
